import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Trophy, ListChecks, Mic, MessagesSquare, ChevronLeft, FileText, Download, Linkedin, ShieldAlert } from "lucide-react";
import api from "../../api";
import { buildLinkedInAddToProfileUrl } from "../../utils/linkedin";
import ProctorGate from "../../components/ProctorGate";
import GroundRulesModal from "../../components/GroundRulesModal";

const SECTION_META = {
  mcq: {
    icon: ListChecks,
    tagline: "10 MCQs · 20 min · 2 attempts (best counts)",
    href: "/participant/salescert/mcq",
    cta: "Start MCQ section",
  },
  sales_pitch: {
    icon: Mic,
    tagline: "4 minutes recorded pitch · 2 attempts (best counts)",
    href: "/participant/salescert/pitch",
    cta: "Start Sales Pitch",
  },
  roleplay: {
    icon: MessagesSquare,
    tagline: "5-min AI customer role play · 2 attempts (best counts)",
    href: "/participant/salescert/roleplay",
    cta: "Start Role Play",
  },
};

/** Landing / progress tracker for the VI Sales Certification 2026. */
export default function SalesCertHub() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [dlBusy, setDlBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  useEffect(() => {
    // Only pull the section-status snapshot for the hub. The Proctor Gate no
    // longer runs on hub render — it's applied inside each section entry so
    // candidates can browse the hub freely without a re-verify each visit.
    api.get("/participant/salescert/status")
      .then((r) => {
        setData(r.data);
        // First-time visitors get the ground-rules modal pinned open. If
        // they've accepted already (assignment doc has ground_rules_accepted_at)
        // we skip the auto-open — they can still reopen it from the ribbon.
        if (!r.data.ground_rules_accepted_at) setRulesOpen(true);
      })
      .catch((e) => setErr(e?.response?.data?.detail || String(e)));
  }, []);

  const rulesAccepted = !!data?.ground_rules_accepted_at;
  const startBlocked = !rulesAccepted;

  const downloadCertificate = async () => {
    setDlBusy(true);
    try {
      const r = await api.get("/participant/salescert/certificate.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "VI_SalesCert_2026_Certificate.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDlBusy(false);
    }
  };

  if (err) {
    return (
      <div className="max-w-2xl mx-auto p-8 fade-in">
        <h2 className="font-heading text-2xl font-bold mb-2">Could not load</h2>
        <p className="text-sm text-[#525252]">{err}</p>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-sm text-[#878787]">Loading…</div>;

  const pct = Math.round((data.marks_awarded / (data.marks_max || 1)) * 100);
  const allDone = data.completed_count === data.total_count;
  const released = data.results_released !== false; // treat undefined as released (legacy)
  // Any section already submitted? (used to gate the pre-release info strip)
  const anyRecorded = (data.sections || []).some(
    (s) => s.status === "completed" || s.status === "recorded"
  );

  return (
    <div className="max-w-5xl mx-auto p-2 space-y-8 fade-in" data-testid="salescert-hub">
      <GroundRulesModal
        open={rulesOpen}
        initialAccepted={data?.ground_rules_accepted_at}
        onDismiss={() => setRulesOpen(false)}
        onAccepted={(ts) => {
          setData((d) => (d ? { ...d, ground_rules_accepted_at: ts } : d));
          setRulesOpen(false);
        }}
      />

      <button
        onClick={() => nav("/participant")}
        className="text-xs uppercase tracking-widest text-[#878787] hover:text-[#0A0A0A] flex items-center gap-1"
        data-testid="salescert-back"
      >
        <ChevronLeft size={14} /> All programmes
      </button>

      {/* Pinned Ground-Rules ribbon — a compact 2-line strip candidates can
          re-open the full modal from anytime. Colour flips red when the
          rules haven't been accepted yet, so the first-time visitor
          understands they can't start any section until they read them. */}
      <div
        className={`p-3 sm:p-4 flex items-start sm:items-center gap-3 border-l-4 ${
          rulesAccepted
            ? "border-[#059669] bg-[#F0FDF4]"
            : "border-[#EE2737] bg-[#FEF2F2]"
        }`}
        data-testid="salescert-rules-ribbon"
      >
        <ShieldAlert size={20} className={rulesAccepted ? "text-[#059669]" : "text-[#EE2737]"} />
        <div className="flex-1 text-xs sm:text-sm leading-relaxed">
          <strong>Ground rules · </strong>
          {rulesAccepted ? (
            <>Same mic · Own voice only · No tab-switching · No screenshots</>
          ) : (
            <>Please read the do&apos;s and don&apos;ts before you start any section — Start buttons are locked until you accept.</>
          )}
        </div>
        <button
          onClick={() => setRulesOpen(true)}
          className="text-[10px] sm:text-xs uppercase tracking-widest font-bold text-[#EE2737] underline whitespace-nowrap"
          data-testid="salescert-rules-open"
        >
          {rulesAccepted ? "Read again" : "Read now"}
        </button>
      </div>

      <div>
        <div className="overline mb-2">VI SALES CERTIFICATION 2026 · 3 SECTIONS · 30 MIN</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">Your certification journey</h1>
        <p className="text-sm text-[#525252] mt-2 max-w-2xl leading-relaxed">
          Complete all three sections in any order. Each section allows up to 2 attempts — the best score
          counts. Your certificate will be shared once VI L&amp;D releases results.
        </p>
      </div>

      {/* Score summary strip — stacks on mobile, side-by-side ≥ sm.
          Hidden entirely until VI L&D releases results. The pre-release
          embargo note only appears once the candidate has actually recorded
          at least one section, so a fresh candidate isn't confused by it. */}
      {released ? (
        <div className="card p-6 flex flex-wrap items-center gap-6 sm:gap-8" data-testid="salescert-summary">
          <div>
            <div className="overline mb-1">AGGREGATE</div>
            <div className="flex items-baseline gap-2">
              <div className="text-4xl sm:text-5xl font-black text-[#EE2737]" data-testid="salescert-total-marks">
                {data.marks_awarded}
              </div>
              <div className="text-sm text-[#525252]">/ {data.marks_max}</div>
            </div>
            <div className="text-xs text-[#878787] mt-1">{pct}% of total marks awarded so far</div>
          </div>
          <div className="hidden sm:block border-l border-[#E5E7EB] h-14" />
          <div>
            <div className="overline mb-1">SECTIONS DONE</div>
            <div className="text-2xl font-black">
              <span data-testid="salescert-completed-count">{data.completed_count}</span> / {data.total_count}
            </div>
          </div>
          <div className="hidden sm:block border-l border-[#E5E7EB] h-14" />
          <div>
            <div className="overline mb-1">STATUS</div>
            <div className="flex items-center gap-2">
              {data.certified ? (
                <>
                  <Trophy size={20} className="text-[#059669]" />
                  <span className="font-heading font-bold text-lg text-[#059669]" data-testid="salescert-status-badge">Certified</span>
                </>
              ) : allDone ? (
                <span className="font-heading font-bold text-lg text-[#B45309]" data-testid="salescert-status-badge">Below threshold</span>
              ) : (
                <span className="font-heading font-bold text-lg text-[#525252]" data-testid="salescert-status-badge">In progress</span>
              )}
            </div>
          </div>
        </div>
      ) : anyRecorded ? (
        <div
          className="card p-6 border-l-4 border-[#5F004B] bg-[#5F004B]/5"
          data-testid="salescert-results-embargo"
        >
          <div className="overline mb-2 !text-[#5F004B]">RESULTS NOT YET RELEASED</div>
          <p className="text-sm text-[#0A0A0A] leading-relaxed max-w-2xl">
            Your responses have been recorded. VI L&amp;D will share your consolidated report
            and certificate once the assessment window closes — you will receive an email
            with your report link the moment results go live.
          </p>
        </div>
      ) : null}

      {/* Section cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data.sections.map((s, idx) => {
          const meta = SECTION_META[s.key];
          const Icon = meta.icon;
          const done = s.status === "completed" || s.status === "recorded";
          return (
            <div
              key={s.key}
              className={`card p-6 flex flex-col ${done ? "border-2 border-[#059669]/40" : ""}`}
              data-testid={`salescert-section-${s.key}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A] text-white">
                  <Icon size={18} />
                </div>
                <span className={`badge ${done ? "badge-success" : "badge-neutral"}`}>
                  {done ? (released ? "Done" : "Recorded") : "Not started"}
                </span>
              </div>
              <div className="overline mb-1">SECTION {idx + 1} · {s.marks_max} MARKS</div>
              <h3 className="font-heading font-bold text-lg mb-1">{s.label}</h3>
              <p className="text-xs text-[#878787] mb-4">
                <Clock size={11} className="inline mr-1" />
                {s.duration_min} min · {meta.tagline}
              </p>

              {/* Attempts summary — visible whenever the section has any
                  attempt state so the candidate always knows where they
                  stand. Applies to every section with `attempts_used` set
                  (mcq, sales_pitch, roleplay all support up to 2 attempts). */}
              {(s.attempts_used || 0) > 0 && s.attempts_max && (
                <div
                  className="mb-3 text-[10px] uppercase tracking-widest font-bold text-[#5F004B]"
                  data-testid={`salescert-${s.key}-attempts-summary`}
                >
                  Attempts: {s.attempts_used} of {s.attempts_max}
                  {s.can_retake
                    ? <span className="ml-1 text-[#EE2737]">· {s.attempts_max - s.attempts_used} remaining</span>
                    : <span className="ml-1 text-[#878787]">· best score kept</span>}
                </div>
              )}

              {done ? (
                <div className="mt-auto">
                  {released ? (
                    <>
                      <div className="flex items-baseline gap-2 mb-3">
                        <div className="text-3xl font-black text-[#059669]" data-testid={`salescert-marks-${s.key}`}>
                          {s.marks_awarded}
                        </div>
                        <div className="text-xs text-[#525252]">/ {s.marks_max}</div>
                      </div>
                      <span className="btn btn-secondary !cursor-default opacity-70 justify-center w-full text-xs">
                        <CheckCircle2 size={12} /> Completed
                      </span>
                    </>
                  ) : (
                    <>
                      {s.can_retake ? (
                        startBlocked ? (
                          <button
                            onClick={() => setRulesOpen(true)}
                            className="btn btn-primary justify-center w-full text-xs mb-2"
                            data-testid={`salescert-retake-${s.key}`}
                          >
                            <ShieldAlert size={12} /> Read rules to retake
                          </button>
                        ) : (
                          <Link
                            to={meta.href}
                            className="btn btn-primary justify-center w-full text-xs mb-2"
                            data-testid={`salescert-retake-${s.key}`}
                          >
                            Take attempt {(s.attempts_used || 1) + 1} of {s.attempts_max || 2}
                            <ArrowRight size={12} />
                          </Link>
                        )
                      ) : null}
                      <span
                        className="btn btn-secondary !cursor-default opacity-70 justify-center w-full text-xs"
                        data-testid={`salescert-recorded-${s.key}`}
                      >
                        <CheckCircle2 size={12} /> Recorded
                      </span>
                    </>
                  )}
                </div>
              ) : (
                startBlocked ? (
                  <button
                    onClick={() => setRulesOpen(true)}
                    className="btn btn-primary justify-center mt-auto text-xs"
                    data-testid={`salescert-start-${s.key}`}
                    title="Please accept the Ground Rules first"
                  >
                    <ShieldAlert size={12} /> Read rules to start
                  </button>
                ) : (
                  <Link
                    to={meta.href}
                    className="btn btn-primary justify-center mt-auto text-xs"
                    data-testid={`salescert-start-${s.key}`}
                  >
                    {meta.cta} <ArrowRight size={12} />
                  </Link>
                )
              )}
            </div>
          );
        })}
      </div>

      {/* Report + Certificate CTAs — hidden until L&D releases results */}
      {released && data.completed_count > 0 && (
        <div className="flex flex-wrap gap-3 items-center pt-4 border-t border-[#E5E7EB]" data-testid="salescert-hub-footer">
          <Link to="/participant/salescert/report" className="btn btn-secondary text-xs" data-testid="salescert-view-report">
            <FileText size={14} /> View detailed report
          </Link>
          {data.certified && (
            <>
              <button
                onClick={downloadCertificate}
                disabled={dlBusy}
                className="btn btn-primary text-xs"
                data-testid="salescert-download-cert"
              >
                <Download size={14} /> {dlBusy ? "Preparing…" : "Download certificate"}
              </button>
              <a
                href={buildLinkedInAddToProfileUrl({
                  certId: data.cert_id,
                  verifyUrl: `${window.location.origin}/verify/${data.cert_id}`,
                  issuedAtIso: data.sections
                    ?.map((s) => s.submitted_at)
                    .filter(Boolean)
                    .sort()
                    .pop(),
                })}
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary text-xs !bg-[#0A66C2] !text-white !border-transparent hover:!opacity-90"
                data-testid="salescert-linkedin"
              >
                <Linkedin size={14} /> Add to LinkedIn
              </a>
              {data.cert_id && (
                <div className="text-[11px] text-[#525252] ml-2" data-testid="salescert-cert-id-hint">
                  <span className="uppercase tracking-widest font-bold text-[#878787]">Cert ID · </span>
                  <span className="font-mono text-[#0A0A0A]">{data.cert_id}</span>
                  <span className="text-[#878787]"> · Verify at </span>
                  <a
                    href={`/verify/${data.cert_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#EE2737] underline"
                    data-testid="salescert-verify-link"
                  >/verify/{data.cert_id}</a>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
